import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import React from "react";
import { takeFirst } from "~/features/app/utils";
import { HardwareComparisonTableDesktop } from "~/features/blocks/inventory/hardware-comparison-table/desktop";
import { HardwareComparisonTableMobile } from "~/features/blocks/inventory/hardware-comparison-table/hardware-comparison-table-mobile";
import { type Mode, type Theme, getThemeStyle } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { graphql } from "~/gql/runtime";

// View-contract fragment consumed by the product cards
// (`features/card/product-card.tsx`, `product-featured-card.tsx`). It selects
// the hardware spec fields those cards show as tags. The comparison-table
// adapter uses section-level spec values instead.
export const hardwareSpecsFragment = graphql(`
  fragment HardwareSpecs on hardware_default_Entry {
    productFovType
    productRange
    productPrecisionUpTo
    productVerticalResolutionUpTo
    productHorizontalResolutionUpTo
    productWeight
    productVerticalResolution
    productMaximumRepresentableRange
    productVerticalFieldOfView
    productVerticalAngularResolution
    productHorizontalResolution
    productHorizontalFieldOfView
    productHorizontalAngularResolution
    productPointsPerSecond
    productFrameRate
    productNumberOfReturns
    productIngressProtection
    productTypicalPowerConsumption
    productOperatingVoltage
    productOperatingTemperature
    productTimeSynchronization
    productEmbeddedImu
    productMinimumRange
    productNearIrAmbientData
    productEthernetOptions
    productBeamConfigurationOptions
  }
`);
type PlainAsset = Record<string, unknown>;
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type HardwareComparisonRow = {
  key?: string;
  label?: string;
  icon?: string;
  customIcon?: PlainAsset;
};
export type HardwareComparisonSpecValue = {
  specKey?: string;
  value?: string;
};
export type HardwareComparisonProduct = {
  id?: string;
  url?: string;
  downloadSpecsLink?: string;
  title?: string;
  productCaption?: string;
  productImage?: PlainAsset;
  productHighlights?: string[];
  specValues?: HardwareComparisonSpecValue[];
};

// Values are keyed by `spec._key` on the section's specs so rows can be reordered
// or renamed on the section without breaking the lookup.
export function getSpecValue(product: HardwareComparisonProduct, rowKey: string | undefined): string {
  if (!rowKey) return "";
  return product.specValues?.find(sv => sv.specKey === rowKey)?.value ?? "";
}
export type HardwareComparisonTableBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  hasHighlightDifferencesToggle?: boolean | null;
  hasTopDivider?: boolean | null;
  mode?: string | null;
  products?: HardwareComparisonProduct[] | null;
  rows?: HardwareComparisonRow[] | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function HardwareComparisonTableBlockView({
  anchor,
  hasHighlightDifferencesToggle,
  hasTopDivider,
  mode = "light",
  products = [],
  rows = [],
  sectionProps,
  theme = "white"
}: HardwareComparisonTableBlockViewProps) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const items = products ?? [];
  const rowItems = rows ?? [];
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, themeStyle.background]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <HardwareComparisonTableDesktop mode={mode} theme={theme} products={items} rows={rowItems} hasHighlightDifferencesToggle={hasHighlightDifferencesToggle} />
        </_StyledDiv>
        <_StyledDiv2>
          <HardwareComparisonTableMobile mode={mode} theme={theme} products={items} rows={rowItems} hasHighlightDifferencesToggle={hasHighlightDifferencesToggle} />
        </_StyledDiv2>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-vr1y0s-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-vr1y0s-1"
})({
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledDiv2 = /*#__PURE__*/_styled3("div").withConfig({
  displayName: "hardware-comparison-table___StyledDiv2",
  componentId: "sc-81wk5-0"
})({
  "display": "block",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});